import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Laptop } from 'lucide-react';
import syncApi from '../api/sync';
import { errorMessage } from '../api/drive';
import { useAuth } from '../contexts/AuthProvider';
import { LampMark } from '../components/drive/AppShell';

const PLATFORM = { macos: 'Mac', windows: 'Windows PC', linux: 'Linux computer' };

/** Opened by the desktop app: approve signing that computer in to this account. */
const DesktopLinkPage = () => {
  const [params] = useSearchParams();
  const code = params.get('code') || '';
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    syncApi.linkInfo(code)
      .then((res) => { setInfo(res); setState('ask'); })
      .catch((err) => { setError(errorMessage(err, 'This sign-in request is not valid.')); setState('error'); });
  }, [code]);

  const answer = async (approve) => {
    try {
      await syncApi.approve(code, approve);
      setState(approve ? 'approved' : 'denied');
    } catch (err) {
      setError(errorMessage(err, 'Something went wrong.'));
      setState('error');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sidebar px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-background p-6 text-center shadow-sm">
        <div className="mb-4 flex justify-center"><LampMark size={40} /></div>
        {state === 'loading' && <p className="text-muted-foreground">Checking the request…</p>}
        {state === 'ask' && (
          <>
            <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-muted"><Laptop className="h-5 w-5" aria-hidden="true" /></span>
            <h1 className="text-base font-semibold">Sign in the desktop app?</h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              “{info.device_name}”{PLATFORM[info.platform] ? ` (${PLATFORM[info.platform]})` : ''} wants to sync your LampTwist Drive files as <b className="text-foreground">{user?.email}</b>.
            </p>
            <p className="mt-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">Code <span className="font-semibold tracking-wider text-foreground">{code}</span> should match the one shown in the app.</p>
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => answer(false)} className="h-9 flex-1 rounded-lg border border-border text-[13.5px] font-medium hover:bg-muted">Deny</button>
              <button type="button" onClick={() => answer(true)} className="h-9 flex-1 rounded-lg bg-primary text-[13.5px] font-medium text-primary-foreground hover:bg-primary/85">Allow</button>
            </div>
          </>
        )}
        {state === 'approved' && (
          <>
            <h1 className="text-base font-semibold">You’re signed in</h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">Go back to the LampTwist Drive app — it starts syncing in a moment. You can close this tab.</p>
          </>
        )}
        {state === 'denied' && (
          <>
            <h1 className="text-base font-semibold">Request denied</h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">That computer was not signed in.</p>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 className="text-base font-semibold">Can’t sign in this computer</h1>
            <p className="mt-1 text-[13.5px] text-muted-foreground">{error} Start again from the app.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopLinkPage;
